/**
 * DetailFenster — gemeinsame Bausteine für klare Detailansichten im Strato-Stil.
 *
 * Aufbau einer Detailansicht (Aufgabe, später auch Termin):
 *   <DetailWerkzeugleiste aktionen={[…]} mehr={[…]} />   Symbolleiste oben (Bearbeiten, Erledigt, Löschen …)
 *   <DetailBrotkrumen teile={[…]} />                     Pfad, z. B. Projekt › Hauptaufgabe
 *   <DetailTitel … />                                    großer Titel, im Bearbeiten-Modus ein Eingabefeld
 *   <FeldRaster>                                         zweispaltig: Beschriftung rechtsbündig | Wert
 *     <Feld label="Status">…</Feld>
 *   </FeldRaster>
 *   <DetailAbschnitt titel="Unteraufgaben">…</DetailAbschnitt>
 *
 * Die Bausteine kennen keine Fachlogik. Aufgaben- und Terminfenster setzen sie nur zusammen,
 * damit alle Detailansichten gleich aussehen und sich gleich bedienen lassen.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, MoreHorizontal, Search, X } from 'lucide-react';

export const DETAIL_FARBEN = {
  titel: 'text-[#1E3A5F]',
  akzent: 'border-l-[#E67E22]',
};

/** Symbolleiste: Icon-Schaltflächen links, optional „Mehr“-Menü (…) */
export function DetailWerkzeugleiste({ aktionen = [], mehr = [], rechts = null }) {
  const [mehrOffen, setMehrOffen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!mehrOffen) return undefined;
    const zu = (e) => { if (ref.current && !ref.current.contains(e.target)) setMehrOffen(false); };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [mehrOffen]);

  const sichtbareMehr = mehr.filter(Boolean);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="detail-werkzeugleiste">
      {aktionen.filter(Boolean).map((a) => {
        const Icon = a.icon;
        return (
          <button
            key={a.key}
            type="button"
            title={a.label}
            aria-label={a.label}
            onClick={a.onClick}
            disabled={a.disabled}
            data-testid={a.testid || `detail-aktion-${a.key}`}
            className={`h-9 min-w-[36px] px-2 rounded-md flex items-center justify-center gap-1 text-[#2B2FB5] hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent ${a.aktiv ? 'bg-gray-100' : ''} ${a.gefahr ? 'hover:text-red-600' : ''}`}
          >
            {a.inhalt || <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />}
          </button>
        );
      })}
      {sichtbareMehr.length > 0 && (
        <div className="relative" ref={ref}>
          <button
            type="button"
            title="Weitere Aktionen"
            aria-label="Weitere Aktionen"
            onClick={() => setMehrOffen((o) => !o)}
            className="h-9 w-9 rounded-md flex items-center justify-center text-[#2B2FB5] hover:bg-gray-100"
            data-testid="detail-aktion-mehr"
          >
            <MoreHorizontal className="w-[18px] h-[18px]" />
          </button>
          {mehrOffen && (
            <div className="absolute left-0 top-10 z-30 w-60 bg-white border border-gray-200 rounded-md shadow-lg py-1">
              {sichtbareMehr.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => { setMehrOffen(false); m.onClick?.(); }}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${m.gefahr ? 'text-red-600' : 'text-gray-700'}`}
                    data-testid={`detail-mehr-${m.key}`}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    {m.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      {rechts && <div className="ml-auto flex items-center gap-2">{rechts}</div>}
    </div>
  );
}

/** Pfad über dem Titel; Teile mit onClick sind anklickbar */
export function DetailBrotkrumen({ teile = [] }) {
  const liste = teile.filter(Boolean);
  if (liste.length === 0) return null;
  return (
    <nav className="flex items-center flex-wrap gap-1 text-xs text-gray-500" aria-label="Pfad" data-testid="detail-brotkrumen">
      {liste.map((t, i) => (
        <span key={t.key || i} className="flex items-center gap-1 min-w-0">
          {i > 0 && <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />}
          {t.onClick ? (
            <button type="button" onClick={t.onClick} className="text-[#2B2FB5] hover:underline truncate max-w-[220px]">{t.label}</button>
          ) : (
            <span className="truncate max-w-[220px]">{t.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** Großer Titel; im Bearbeiten-Modus ein schlichtes Eingabefeld in gleicher Größe */
export function DetailTitel({ wert, bearbeiten = false, onChange, platzhalter = 'Titel', durchgestrichen = false, eingabe = null }) {
  if (bearbeiten) {
    return eingabe || (
      <input
        type="text"
        value={wert}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={platzhalter}
        autoFocus
        className={`w-full text-2xl font-bold ${DETAIL_FARBEN.titel} bg-transparent border-0 border-b-2 border-[#2B2FB5]/40 focus:border-[#2B2FB5] focus:outline-none px-0 py-1`}
        data-testid="detail-titel-eingabe"
      />
    );
  }
  return (
    <h2 className={`text-2xl font-bold ${DETAIL_FARBEN.titel} break-words ${durchgestrichen ? 'line-through opacity-60' : ''}`} style={{ textWrap: 'balance' }} data-testid="detail-titel">
      {wert}
    </h2>
  );
}

/** Zweispaltiges Raster: Beschriftung rechtsbündig, Wert linksbündig (auf dem Handy untereinander) */
export function FeldRaster({ children, className = '' }) {
  return (
    <dl className={`grid grid-cols-1 sm:grid-cols-[150px_minmax(0,1fr)] gap-x-5 gap-y-3 sm:gap-y-3.5 items-baseline max-w-[680px] ${className}`}>
      {children}
    </dl>
  );
}

export function Feld({ label, children, testid, oben = false }) {
  return (
    <>
      <dt className={`text-sm font-semibold ${DETAIL_FARBEN.titel} sm:text-right ${oben ? 'sm:self-start sm:pt-1.5' : ''}`}>{label}</dt>
      <dd className="m-0 min-w-0 text-sm text-gray-800 -mt-2 sm:mt-0" data-testid={testid}>{children}</dd>
    </>
  );
}

/** Leerer Wert in Feldern */
export function LeerWert({ text = '–' }) {
  return <span className="text-gray-400">{text}</span>;
}

/** Kleines Status-Etikett in Großbuchstaben (wie „NICHT BEGONNEN“ bei Strato) */
export function StatusEtikett({ klasse = 'bg-gray-100 text-gray-600', children, testid }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10.5px] uppercase tracking-wide whitespace-nowrap ${klasse}`} data-testid={testid}>
      {children}
    </span>
  );
}

export function Fortschrittsbalken({ prozent = 0, breit = true }) {
  const p = Math.max(0, Math.min(100, Math.round(prozent)));
  return (
    <div className={`h-1.5 rounded-full bg-[#d9d8cf] overflow-hidden ${breit ? 'max-w-[320px] mt-1.5' : 'w-11 inline-block align-middle'}`}>
      <div className="h-full bg-[#2B2FB5] transition-[width] duration-200" style={{ width: `${p}%` }} />
    </div>
  );
}

/** Abschnitt mit Überschrift und dünner Linie; optional aufklappbar */
export function DetailAbschnitt({ titel, rechts = null, children, aufklappbar = false, standardOffen = true, testid }) {
  const [offen, setOffen] = useState(standardOffen);
  return (
    <section className="max-w-[680px]" data-testid={testid}>
      <div className="flex items-baseline justify-between border-b border-gray-200 pb-1.5 mb-2">
        {aufklappbar ? (
          <button type="button" onClick={() => setOffen((o) => !o)} className={`flex items-center gap-1 text-[15px] font-semibold ${DETAIL_FARBEN.titel}`}>
            {offen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {titel}
          </button>
        ) : (
          <h3 className={`text-[15px] font-semibold ${DETAIL_FARBEN.titel}`}>{titel}</h3>
        )}
        {rechts && <div className="text-xs text-gray-500">{rechts}</div>}
      </div>
      {(!aufklappbar || offen) && children}
    </section>
  );
}

/** Einheitliche Klassen für Eingabefelder im Bearbeiten-Modus */
export const EINGABE_KLASSE = 'rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-[#2B2FB5] focus:outline-none focus:ring-1 focus:ring-[#2B2FB5]';

/** Suchfeld mit Vorschlagsliste (Adresse, Projekt, Dokument) */
export function SuchAuswahl({ platzhalter, suche, setSuche, laedt, eintraege, onWaehle, beschriftung, unterzeile, testid }) {
  const [offen, setOffen] = useState(false);
  return (
    <div className="relative max-w-[380px]">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
      <input
        type="text"
        value={suche}
        onChange={(e) => { setSuche(e.target.value); setOffen(true); }}
        onFocus={() => setOffen(true)}
        onBlur={() => setTimeout(() => setOffen(false), 150)}
        placeholder={platzhalter}
        className={`${EINGABE_KLASSE} w-full pl-7`}
        data-testid={testid}
      />
      {offen && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
          {laedt ? (
            <div className="flex justify-center p-3"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div>
          ) : eintraege.length === 0 ? (
            <p className="p-3 text-xs text-gray-500 text-center">Keine Treffer</p>
          ) : eintraege.slice(0, 8).map((e) => (
            <button
              key={e.id}
              type="button"
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => { onWaehle(e); setOffen(false); }}
              className="w-full px-3 py-1.5 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
            >
              <p className="text-sm text-gray-900">{beschriftung(e)}</p>
              {unterzeile?.(e) && <p className="text-xs text-gray-500">{unterzeile(e)}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function GewaehlterEintrag({ text, unter, onEntfernen, href }) {
  return (
    <span className="inline-flex items-center gap-2 max-w-full">
      {href ? <Link to={href} className="text-[#2B2FB5] hover:underline truncate">{text}</Link> : <span className="truncate">{text}</span>}
      {unter && <span className="text-xs text-gray-500 truncate">{unter}</span>}
      {onEntfernen && (
        <button type="button" onClick={onEntfernen} className="text-gray-400 hover:text-red-500" aria-label="Verknüpfung entfernen">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

/**
 * Eigenschaften ein-/ausklappen (wie im Notizbuch): merkt sich den Zustand je Bereich im Browser.
 *   const [offen, umschalten] = useEigenschaftenOffen('notizbuch-details');
 */
export function useEigenschaftenOffen(speicherKey, standardOffen = false) {
  const [offen, setOffen] = useState(() => {
    try {
      const v = localStorage.getItem(speicherKey);
      return v === null ? standardOffen : v === '1';
    } catch { return standardOffen; }
  });
  const umschalten = () => setOffen((o) => {
    try { localStorage.setItem(speicherKey, o ? '0' : '1'); } catch { /* ignorieren */ }
    return !o;
  });
  return [offen, umschalten];
}

/**
 * Kopfzeile „Eigenschaften“ mit Pfeil; zugeklappt zeigt sie eine Kurzfassung
 * (z. B. „Ohne Ordner · #tag · 1 Projekt“). Darunter die Felder nur, wenn offen.
 */
export function EigenschaftenKopf({ offen, onUmschalten, zusammenfassung = [], testid = 'eigenschaften', children }) {
  const kurz = zusammenfassung.filter(Boolean).join(' · ');
  return (
    <>
      <button
        type="button"
        onClick={onUmschalten}
        className="flex items-center gap-1.5 w-full min-w-0 text-left text-xs text-gray-500 hover:text-[#1E3A5F] py-1"
        aria-expanded={offen}
        title={offen ? 'Eigenschaften einklappen' : 'Eigenschaften anzeigen'}
        data-testid={`${testid}-toggle`}
      >
        {offen ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
        <span className="font-medium shrink-0">Eigenschaften</span>
        {!offen && kurz && <span className="truncate text-gray-400">{kurz}</span>}
      </button>
      {offen && (
        <div className="max-h-[40vh] overflow-y-auto pt-1 pb-2" data-testid={testid}>
          {children}
        </div>
      )}
    </>
  );
}
